import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wine, Clock, Users, ChevronRight, Loader2, QrCode, BookOpen, Sparkles } from 'lucide-react';
import axios from 'axios';

const SOMMELIER_AVATAR = "https://customer-assets.emergentagent.com/job_wine-sommelier-2/artifacts/vu5iglcz_att.ynPmhaFhSeLs5blyB0W6yY3b31D9r_dMDQ9FnJVxofo.jpeg";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RestaurantSelect = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleDemo = (restaurant) => {
    navigate(`/${restaurant}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!restaurantName || !email || !phone) {
      setSubmitStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        restaurant_name: restaurantName,
        email: email,
        phone: phone
      });
      setSubmitStatus({ type: 'success', message: response.data.message });
      setRestaurantName('');
      setEmail('');
      setPhone('');
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex flex-col items-center leading-none mx-auto">
            <span className="text-[10px] font-medium tracking-[0.3em] text-gray-400">THE</span>
            <span className="text-lg font-semibold tracking-wide" style={{fontFamily: 'Georgia, serif', color: '#722F37'}}>SOMM</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 pt-16 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          {/* Avatar */}
          <div className="mb-8 flex justify-center">
            <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-white shadow-lg">
              <img 
                src={SOMMELIER_AVATAR}
                alt="Your Digital Sommelier"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight" style={{fontFamily: 'Georgia, serif'}}>
            Your Digital Sommelier.
            <br />
            <span className="text-[#722F37]">Designed to Increase Wine Sales.</span>
          </h1>
          <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto leading-relaxed mb-8" style={{fontFamily: 'Georgia, serif'}}>
            Guests scan a QR code, answer a few questions, and get instant wine recommendations tailored to your menu.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a 
              href="#demo" 
              className="bg-[#D4704A] text-white px-7 py-3 rounded-full text-center font-semibold hover:bg-[#B85A3A] transition-colors shadow-md text-sm"
              style={{fontFamily: 'Georgia, serif'}}
            >
              Try Live Demo
            </a>
            <a 
              href="#contact" 
              className="bg-white text-gray-700 border border-gray-200 px-7 py-3 rounded-full text-center font-semibold hover:border-[#D4704A] hover:text-[#D4704A] transition-all text-sm"
              style={{fontFamily: 'Georgia, serif'}}
            >
              Get Started
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="px-4 pb-16">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="text-3xl font-bold text-[#722F37] mb-1" style={{fontFamily: 'Georgia, serif'}}>23%</div>
              <p className="text-xs text-gray-400" style={{fontFamily: 'Georgia, serif'}}>Average increase in wine sales</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="text-3xl font-bold text-[#722F37] mb-1" style={{fontFamily: 'Georgia, serif'}}>24/7</div>
              <p className="text-xs text-gray-400" style={{fontFamily: 'Georgia, serif'}}>Always available for your guests</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Restaurants Choose THE SOMM */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center" style={{fontFamily: 'Georgia, serif'}}>
            Why Restaurants Choose THE SOMM
          </h2>
          <p className="text-base text-gray-400 max-w-lg mx-auto text-center mb-10" style={{fontFamily: 'Georgia, serif'}}>
            Transform your wine program with a sommelier that knows your cellar inside and out
          </p>
          
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0 mt-0.5">
                <Wine className="h-5 w-5 text-[#722F37]" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Georgia, serif'}}>Increase Wine Sales</h3>
                <p className="text-sm text-gray-500 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>Guests who engage with THE SOMM order more wine and trade up to premium bottles.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Georgia, serif'}}>Save Staff Time</h3>
                <p className="text-sm text-gray-500 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>Free your servers to focus on service while guests explore your wine list independently.</p>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" style={{fontFamily: 'Georgia, serif'}}>Enhance Guest Experience</h3>
                <p className="text-sm text-gray-500 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>Personalized recommendations make every guest feel like a VIP with a private sommelier.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center" style={{fontFamily: 'Georgia, serif'}}>
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-5 w-5 text-sky-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2" style={{fontFamily: 'Georgia, serif'}}>
                Share Your Wine List
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>
                Send us your wine list and menu. We'll build your custom sommelier.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                <Users className="h-5 w-5 text-rose-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2" style={{fontFamily: 'Georgia, serif'}}>
                We Train Your SOMM
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>
                We train your sommelier on your wines, pairings, and house style in detail.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <QrCode className="h-5 w-5 text-green-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2" style={{fontFamily: 'Georgia, serif'}}>
                Launch & Grow Sales
              </h3>
              <p className="text-xs text-gray-400 leading-relaxed" style={{fontFamily: 'Georgia, serif'}}>
                Place QR codes on tables or wine lists. Watch wine sales increase.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="px-4 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center" style={{fontFamily: 'Georgia, serif'}}>
            Try a Live Demo
          </h2>
          <p className="text-base text-gray-400 text-center mb-8" style={{fontFamily: 'Georgia, serif'}}>
            Experience THE SOMM with real restaurant wine lists
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => handleDemo('treadwell')}
              data-testid="demo-treadwell"
              className="bg-white rounded-2xl p-5 text-left border border-gray-100 hover:border-[#D4704A] hover:shadow-md transition-all duration-200 group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-[#722F37] rounded-xl flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-lg" style={{fontFamily: 'Georgia, serif'}}>A</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-[#722F37] transition-colors" style={{fontFamily: 'Georgia, serif'}}>
                    Restaurant A
                  </h3>
                  <p className="text-xs text-gray-400" style={{fontFamily: 'Georgia, serif'}}>
                    Fine Dining &middot; Niagara-on-the-Lake
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#D4704A] group-hover:translate-x-1 transition-all shrink-0" />
              </div>
            </button>
            
            <button
              onClick={() => handleDemo('notl')}
              data-testid="demo-notl"
              className="bg-white rounded-2xl p-5 text-left border border-gray-100 hover:border-[#D4704A] hover:shadow-md transition-all duration-200 group"
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-[#722F37] rounded-xl flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-lg" style={{fontFamily: 'Georgia, serif'}}>B</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-[#722F37] transition-colors" style={{fontFamily: 'Georgia, serif'}}>
                    Restaurant B
                  </h3>
                  <p className="text-xs text-gray-400" style={{fontFamily: 'Georgia, serif'}}>
                    Contemporary &middot; Wine Country
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#D4704A] group-hover:translate-x-1 transition-all shrink-0" />
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="px-4 pb-16">
        <div className="max-w-md mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center" style={{fontFamily: 'Georgia, serif'}}>
            Ready to Boost Your Wine Sales?
          </h2>
          <p className="text-base text-gray-400 text-center mb-8" style={{fontFamily: 'Georgia, serif'}}>
            Get in touch and we'll set up your custom sommelier
          </p>
          
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Your Name and Last Name"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                data-testid="contact-name"
                className="w-full px-4 py-3 border border-gray-100 rounded-xl focus:outline-none focus:border-[#D4704A] bg-[#FAFAFA] text-sm"
                style={{fontFamily: 'Georgia, serif'}}
              />
              <input
                type="email"
                placeholder="Your Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="contact-email"
                className="w-full px-4 py-3 border border-gray-100 rounded-xl focus:outline-none focus:border-[#D4704A] bg-[#FAFAFA] text-sm"
                style={{fontFamily: 'Georgia, serif'}}
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="contact-phone"
                className="w-full px-4 py-3 border border-gray-100 rounded-xl focus:outline-none focus:border-[#D4704A] bg-[#FAFAFA] text-sm"
                style={{fontFamily: 'Georgia, serif'}}
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit"
                className="w-full bg-[#D4704A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#B85A3A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm shadow-md"
                style={{fontFamily: 'Georgia, serif'}}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Sending...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
              
              {submitStatus && (
                <div className={`p-3 rounded-xl text-center text-sm ${
                  submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-100' : 'bg-red-50 text-red-800 border border-red-100'
                }`}>
                  {submitStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-4 py-8 border-t border-gray-100">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex flex-col items-center mb-2">
            <span className="text-[8px] font-medium tracking-[0.3em] text-gray-300">THE</span>
            <span className="text-base font-semibold tracking-wide text-gray-300" style={{fontFamily: 'Georgia, serif'}}>SOMM</span>
          </div>
          <p className="text-xs text-gray-300" style={{fontFamily: 'Georgia, serif'}}>
            Your Personal Wine Guide
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RestaurantSelect;
